import { Component, OnDestroy, OnInit, input } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { PwaService } from 'src/app/services/pwa.service';
import {
  IonButtons,
  IonHeader,
  IonTitle,
  IonToolbar,
  Platform,
} from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { IonContent, IonBackButton } from '@ionic/angular/standalone';
import { PermissionType } from 'src/app/enums/permission-type';
import { SettingsControlComponent } from 'src/app/components/settings-control/settings-control.component';
import { PermissionService } from 'src/app/services/permission.service';
import { Subscription } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  standalone: true,
  selector: 'app-permissions',
  templateUrl: './permissions.page.html',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    IonContent,
    IonBackButton,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonTitle,
    SettingsControlComponent,
    NgTemplateOutlet,
  ],
})
export class PermissionsPage implements OnInit, OnDestroy {
  embedded = input(false);
  permissionsForm: FormGroup;
  private resumeSubscription: Subscription;
  readonly PermissionType = PermissionType;

  constructor(
    private fb: FormBuilder,
    private platform: Platform,
    private pwaService: PwaService,
    private router: Router,
    private permissionService: PermissionService
  ) {
    this.createPermissionsForm();
  }

  async ngOnInit() {
    if (this.pwaService.isPwa()) {
      this.router.navigate(['/settings']);
      return;
    }

    this.checkStatus();
    this.resumeSubscription = this.platform.resume.subscribe(() => {
      this.checkStatus();
    });
  }

  ngOnDestroy() {
    this.resumeSubscription.unsubscribe();
  }

  async handleToggle(value: boolean) {
    if (value) {
      const granted = await this.permissionService.requestPermission(
        PermissionType.LOCATION
      );
      if (granted) {
        await this.permissionService.refreshLocationStatus();
        return;
      }
    }
    const confirm =
      await this.permissionService.getOpenSystemSettingsPermission(
        PermissionType.LOCATION
      );
    if (confirm) {
      const action = this.permissionService.getSettingsAction(
        PermissionType.LOCATION
      );
      await action();
    } else {
      this.permissionsForm
        .get('locationEnabled')
        ?.setValue(!value, { emitEvent: false });
    }
  }

  createPermissionsForm() {
    this.permissionsForm = this.fb.group({
      locationEnabled: this.fb.control(false),
    });

    this.permissionsForm
      .get('locationEnabled')
      .valueChanges.subscribe((value) => {
        this.handleToggle(value);
      });
  }

  get locationControl(): FormControl<boolean> {
    return this.permissionsForm.controls
      .locationEnabled as FormControl<boolean>;
  }

  private async checkStatus() {
    const locationEnabled = await this.permissionService.isPermissionEnabled(
      PermissionType.LOCATION
    );

    this.permissionsForm
      .get('locationEnabled')
      ?.setValue(locationEnabled, { emitEvent: false });
  }
}
